import { Component, computed, DestroyRef, inject, input, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { SwalComponent } from '@sweetalert2/ngx-sweetalert2';
import { PanModalComponent } from '../../../../components/pan-modal/pan-modal.component';
import { finalize } from 'rxjs';
import { tryGetErrorMessage } from '../../../../error.utils';
import { LossPreventionService } from '../../../../service/loss-prevention.service';
import { ProblemReason, SubmitErrorRequest } from '../../../../types';

const OTHER_REASON: ProblemReason = { id: 'Other', label: 'Other', code: 'Other' };

@Component({
  selector: 'app-review-report-problem',
  templateUrl: './review-report-problem.component.html',
  styleUrl: './review-report-problem.component.scss',
  standalone: false,
})
export class ReviewReportProblemComponent {
  private destroyRef = inject(DestroyRef);
  private formBuilder = inject(FormBuilder);
  private lpService = inject(LossPreventionService);
  private modal = viewChild<PanModalComponent>('modal');
  private router = inject(Router);
  private reportProblemSuccess = viewChild<SwalComponent>('reportProblemSuccess');

  public reviewId = input<string | undefined>(undefined);

  public readonly isLoadingData = signal(false);
  public readonly loadError = signal<string | undefined>(undefined);

  public selectedErrorTypeIds = signal<string[]>([]);
  public readonly reviewErrorTypes = signal<ProblemReason[]>([OTHER_REASON]);

  public form = this.formBuilder.group({
    comments: [''],
    error_message: [''],
  });

  public comments = toSignal(this.form.controls.comments.valueChanges);
  public errorMessage = toSignal(this.form.controls.error_message.valueChanges);
  public formStatus = toSignal(this.form.statusChanges, { initialValue: this.form.status });

  public reportedErrorTypes = computed(() => this.reviewErrorTypes().filter((type) => this.selectedErrorTypeIds().includes(type.id)));
  public commentsRequired = computed(() => this.reportedErrorTypes().some((type) => type.id === 'Other'));
  public submitRequest = computed<SubmitErrorRequest>(() => ({
    comments: [
      ...this.reportedErrorTypes()
        .filter((reason) => reason.id !== OTHER_REASON.id)
        .map((reason) => reason.label),
      ...(this.commentsRequired() ? [this.comments()?.trim()] : []),
    ]
      .filter(Boolean)
      .join('\n'),
    error_message: this.errorMessage() ?? '',
    errors: this.reportedErrorTypes().map((reason) => ({ is_blocking: false, review_error_type: reason.code ?? 'Other' })),
  }));
  public isErrorFormValid = computed(
    () =>
      !this.isLoadingData() &&
      this.formStatus() === 'VALID' &&
      this.reportedErrorTypes().length > 0 &&
      (!this.commentsRequired() || !!this.comments()?.trim()),
  );

  onReportProblemClick() {
    this.refresh();
    this.modal()?.show();
  }

  refresh() {
    if (this.isLoadingData()) return;
    this.isLoadingData.set(true);
    this.loadError.set(undefined);
    this.selectedErrorTypeIds.set([]);
    this.reviewErrorTypes.set([OTHER_REASON]);
    this.onErrorTypeChange();
    this.lpService
      .getProblemReasons()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isLoadingData.set(false)),
      )
      .subscribe({
        next: (reasons) => this.reviewErrorTypes.set([...reasons, OTHER_REASON]),
        error: (err) => this.loadError.set(tryGetErrorMessage(err)),
      });
  }

  onErrorTypeChange() {
    const comments = this.form.get('comments');
    if (this.commentsRequired()) {
      this.form.controls.error_message.reset();
      comments?.setValidators([Validators.required]);
    } else {
      this.form.controls.error_message.patchValue(
        this.reportedErrorTypes()
          .map((reason) => reason.label)
          .join(', '),
      );
      comments?.clearValidators();
    }
    comments?.updateValueAndValidity();
  }

  updateSelection(value: string, event: any) {
    let errorReports = this.selectedErrorTypeIds();

    if (event.currentTarget.checked) {
      errorReports = [...errorReports, value];
    } else {
      errorReports = errorReports.filter((reason) => reason !== value);
    }

    this.selectedErrorTypeIds.set(errorReports);
    this.onErrorTypeChange();
  }

  onSubmit() {
    const reviewId = this.reviewId();
    if (!reviewId || !this.isErrorFormValid()) return;
    this.lpService.submitError(reviewId, this.submitRequest()).subscribe(
      () => {
        this.modal()?.hide();
        this.reportProblemSuccess()?.fire();
      },
      () => {},
    );
  }

  redirectDashboard() {
    this.router.navigate(['/lp']);
  }
}
